import {
  ShieldCheck,
  Compass,
  Brain,
  ClipboardCheck,
  Network,
  Users,
  Target,
  HelpCircle,
} from "lucide-react";

import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";
import { COMPANY, FAQS_ABOUT } from "@/data/content";

const APPROACH_ITEMS = [
  {
    icon: ShieldCheck,
    title: "Safety-critical learning",
    body:
      "Every programme begins with the question: what does safe performance look like in this role, on this railway, on this day? We design learning that strengthens safety-critical thinking, not just knowledge recall.",
  },
  {
    icon: Compass,
    title: "Operational thinking",
    body:
      "Real railway roles demand judgement under pressure. We develop operational mindsets — how to read a situation, anticipate risk, prioritise actions, and communicate clearly.",
  },
  {
    icon: Brain,
    title: "Human factors and NTS",
    body:
      "Non-technical skills and human factors run through every programme. Situational awareness, workload, fatigue, communication and decision making — embedded, not bolted on.",
  },
  {
    icon: ClipboardCheck,
    title: "Competence development",
    body:
      "We support structured, evidence-based development for individuals, teams and organisations — from foundational learning through to instructor and manager capability.",
  },
  {
    icon: Network,
    title: "Training, risk and assurance compliance",
    body:
      "Through TRAC, we support organisations that need clearer oversight of training delivery, competence evidence, assurance activity, operational risk and compliance controls.",
  },
  {
    icon: Users,
    title: "Recruitment and career preparation",
    body:
      "Through our specialist brands, we prepare aspiring drivers to understand the standards, behaviours and safety-critical expectations associated with train driver recruitment.",
  },
];

const FALLBACK_FAQS = [
  {
    question: "Why was UKTDA founded?",
    answer:
      "UKTDA was founded to improve readiness, competence and safety-critical thinking across the railway sector. The company connects recruitment preparation, professional development, competence evidence, assurance and rail-focused learning into one clearer ecosystem.",
  },
  {
    question: "What makes UKTDA different from other rail training providers?",
    answer:
      "UKTDA focuses on practical railway performance, not just knowledge delivery. Our work is built around operational thinking, human factors, non-technical skills, competence evidence and the behaviours needed in safety-critical railway environments.",
  },
  {
    question: "What does safety-critical training mean?",
    answer:
      "Safety-critical training means developing the judgement, behaviours, communication, discipline and decision-making needed to perform safely in railway roles where errors can have serious operational consequences.",
  },
  {
    question: "Who is UKTDA for?",
    answer:
      "UKTDA supports individuals preparing for railway recruitment and rail-sector organisations looking to strengthen training, competence management, professional development, assurance activity and compliance oversight.",
  },
  {
    question: "Is UKTDA an accredited training organisation?",
    answer:
      "UKTDA provides and facilitates specialist railway training, preparation and professional development. Where accreditation, certification or formal recognition applies to a specific programme, this will be stated clearly on the relevant course or product page.",
  },
];

export default function About() {
  const faqs = Array.isArray(FAQS_ABOUT) && FAQS_ABOUT.length > 0
    ? FAQS_ABOUT
    : FALLBACK_FAQS;

  return (
    <>
      <SEO
        title="About UKTDA | UKTDA"
        description="UKTDA improves readiness, competence and safety-critical thinking across the railway sector through recruitment preparation, training, competence development, TRAC and professional development."
        path="/about"
      />

      <PageHero
        eyebrow="About UKTDA"
        title="Improving readiness, competence and safety-critical thinking across the railway sector."
        intro={`${COMPANY.legalName} (UKTDA) delivers and facilitates specialist railway recruitment, training, competence development, professional development and enterprise assurance support for individuals and rail-sector organisations. Our approach is practical, evidence-based and rooted in operational reality.`}
      />

      <section className="py-16 md:py-24 bg-white" data-testid="about-purpose">
        <div className="container-rail">
          <div className="grid lg:grid-cols-[0.9fr_1.4fr] gap-10 lg:gap-16 items-start">
            <div className="rounded-md bg-navy text-white p-8 border border-navy shadow-sm">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-md bg-safety text-navy">
                <Target className="h-6 w-6" />
              </div>

              <p className="mt-6 label-eyebrow text-safety">
                Our purpose
              </p>

              <h2 className="mt-3 font-display font-bold text-2xl md:text-3xl leading-tight text-white">
                Strengthening the people, systems and evidence that keep the railway safe,
                reliable and professional.
              </h2>
            </div>

            <div>
              <p className="label-eyebrow text-safety">
                Why we exist
              </p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                A specialist rail company connecting recruitment, preparation, competence,
                assurance and professional development.
              </h2>

              <div className="mt-6 space-y-5 text-slate-600 leading-relaxed">
                <p>
                  Across the rail sector, the difference between competent and exceptional
                  performance is rarely about technical knowledge alone.
                </p>

                <p>
                  It is about how people think, communicate, make decisions, manage workload,
                  evidence competence and recover under pressure.
                </p>

                <p>
                  UKTDA exists to support that full journey — from early career preparation
                  through to structured training, competence development, assurance evidence
                  and enterprise-level oversight.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-16 md:py-24 bg-slate-50 border-y border-slate-200"
        data-testid="about-approach"
      >
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">
              Our approach
            </p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Six principles that shape every programme, platform and product we deliver.
            </h2>

            <p className="mt-5 text-slate-600 leading-relaxed">
              UKTDA’s work is built around practical railway performance: how people prepare,
              observe, communicate, make decisions, maintain safe standards and evidence
              competence in normal, degraded and high-pressure situations.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {APPROACH_ITEMS.map((item, index) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="bg-white border border-slate-200 rounded-md p-6 shadow-sm hover:border-safety/60 transition"
                  data-testid={`about-approach-${index}`}
                >
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-navy text-white">
                    <Icon className="h-5 w-5" />
                  </div>

                  <h3 className="mt-5 font-display font-bold text-xl text-navy">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {item.body}
                  </p>
                </article>
              );
            })}
          </div>

        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="about-brands">
        <div className="container-rail">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
            <div>
              <p className="label-eyebrow text-safety">
                UKTDA ecosystem
              </p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                One rail-focused organisation, supporting different stages of development.
              </h2>
            </div>

            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p>
                UKTDA brings together specialist railway brands, learning products and
                enterprise support around a single purpose: improving readiness, competence
                and safety-critical thinking.
              </p>

              <p>
                Our work supports aspiring train drivers, developing railway professionals,
                training teams, instructors, driver managers, safety-critical teams and
                organisations that need stronger oversight of training, competence and
                assurance evidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        items={faqs}
        title="About UKTDA"
        intro="What UKTDA does, why we exist, and how our brands and TRAC platform support railway training, competence and assurance."
        schemaId="faq-about"
        icon={HelpCircle}
      />

      <CTASection
        title="Talk to us about training, preparation, competence or TRAC."
        intro="We support individuals preparing for railway careers and organisations developing training, competence, assurance and safety-critical capability."
        primary={{
          label: "Contact us",
          to: "/contact",
          testId: "about-cta-contact",
        }}
        secondary={{
          label: "Explore TRAC",
          to: "/trac",
          testId: "about-cta-trac",
        }}
      />
    </>
  );
}